import { useState } from 'react';
import { Box } from '@mui/material';
import SettingsSearch from './SettingsSearch';
import SettingsSection from './SettingsSection';
import SettingsRow from './SettingsRow';
import SettingsDropdown from './SettingsDropdown';
import SettingsToggle from './SettingsToggle';
import SettingsDateFormat from './SettingsDateFormat';
import SettingsText from './SettingsText';
import { useUserSetting, useSetUserSetting } from '../../../contexts/UserSettingsContext';
import DateFormatDialog from './DateFormatDialog/DateFormatDialog';
import styleCache from '../../../BodyEditorWidget/Core/StyleCache';
import { THEME_OPTIONS as CODE_EDITOR_THEME_OPTIONS } from '../../../JxControls/JxEdits/JxCodeEditor';
import './SettingsTab.css';

const LANGUAGE_OPTIONS = [
    { label: 'English', value: 'en' },
    { label: 'Nederlands', value: 'nl' },
];

const BGMODE_OPTIONS = [
    { label: 'Dark', value: 'dark' },
    { label: 'Canvas', value: 'canvas' },
];

// ADDED — hardcoded because tabDefinitions.js imports SettingsTab (circular import).
// When adding a new tab, update tabDefinitions.js AND this list.
// See note: "Dashboard Tabs — Edit 2 files"
// admin/log/dev deliberately excluded — admin-only or dev-only tabs, not sensible as a start tab here.
const START_TAB_OPTIONS = [
    { label: 'Account',        value: 'account' },
    { label: 'Your Data',      value: 'yourdata' },
    { label: 'Your Notes',     value: 'yournotes' },
    { label: 'Search',         value: 'search' },
    { label: 'Your Workspace', value: 'library' },
    { label: 'Settings',       value: 'settings' },
];



/**
 * Settings tab — VS2022 property inspector style.
 * Centered max-width container, sticky search, collapsible sections.
 */
export default function SettingsTab() {
    const [search, setSearch] = useState('');

    const language = useUserSetting('language');
    const timeZone = useUserSetting('timeZone');
    const dateFormat = useUserSetting('dateFormat');
    const bgMode = useUserSetting('contentPanel.bgMode');
    const autoOpenJInspector = useUserSetting('contentPanel.autoOpenJInspector');
    // ADDED — close JInspector on mouse leave setting
    const closeOnLeave = useUserSetting('contentPanel.closeJInspectorOnLeave');
    const chipDateTimeFormat = useUserSetting('contentPanel.chipDateTimeFormat');
    const daypartNight = useUserSetting('contentPanel.daypart.night');
    const daypartMorning = useUserSetting('contentPanel.daypart.morning');
    const daypartAfternoon = useUserSetting('contentPanel.daypart.afternoon');
    const daypartEvening = useUserSetting('contentPanel.daypart.evening');
    // ADDED — read configured start tab
    const startTab = useUserSetting('dashboard.startTab');
    // ADDED — when true, tab clicks auto-update dashboard.startTab; when false, start tab
    // is a fixed pin only changeable via the dropdown above
    const rememberLastTab = useUserSetting('dashboard.rememberLastTab');
    // ADDED — body editor style note; options derived from StyleCache (loaded at bootup)
    const bodyEditorStyleNote = useUserSetting('bodyEditor.styleNote');
    const styleNoteOptions = (styleCache.getAll() ?? []).map((s) => ({ label: s.title, value: s.title }));
    // ADDED — JxCodeEditor theme preference (CsCodeTemplateEditorDlg etc.) — options shared with JxCodeEditor itself
    const codeEditorTheme = useUserSetting('codeEditor.theme');
    // ADDED — tooltip on/off + auto-hide duration, consumed directly by JxTooltip
    const tooltipEnabled = useUserSetting('tooltip.enabled');
    const tooltipDurationMs = useUserSetting('tooltip.durationMs');
    // ADDED — OutputMsgDlg date format + cache size
    const msgDateTimeFormat = useUserSetting('msgWindow.dateTimeFormat');
    const msgCacheSize = useUserSetting('msgWindow.cacheSize');
    // ADDED — Load Uml Schema dialog preview thumbnails on/off
    const showPreviewInLoadDlg = useUserSetting('umlWorkspace.showPreviewInLoadDlg');
    const logHoverMessages = useUserSetting('umlWorkspace.logHoverMessages');
    const setSetting = useSetUserSetting();

    const [dateFormatDialogOpen, setDateFormatDialogOpen] = useState(false);
    const [chipDateTimeFormatDialogOpen, setChipDateTimeFormatDialogOpen] = useState(false);
    const [msgDateTimeFormatDialogOpen, setMsgDateTimeFormatDialogOpen] = useState(false);

    const handleChange = (key, val) => {
        setSetting(key, val);
    };

    const handleDateFormatSave = (val) => handleChange('dateFormat', val);

    // Filter rows by search query
    const matches = (label) => label.toLowerCase().includes(search.toLowerCase());
    const anyMatch = (...labels) => !search || labels.some(matches);

    return (
        <Box className="settings-root" sx={{ px: 2 }}>
            <Box className="settings-center">

                {/* Sticky search */}
                <Box className="settings-sticky">
                    <SettingsSearch value={search} onChange={setSearch} />
                </Box>

                {/* Scrollable content */}
                <Box className="settings-scroll">
                    {/* General */}
                    {/* ADDED — tooltip on/off + auto-hide duration, consumed directly by JxTooltip */}
                    {anyMatch('Tooltip Enabled', 'Tooltip Auto-hide') && (
                        <SettingsSection title="General">
                            {matches('Tooltip Enabled') || !search ? (
                                <SettingsRow label="Tooltip Enabled">
                                    <SettingsToggle value={tooltipEnabled} onChange={(val) => handleChange('tooltip.enabled', val)} />
                                </SettingsRow>
                            ) : null}
                            {matches('Tooltip Auto-hide') || !search ? (
                                <SettingsRow label="Tooltip Auto-hide (ms)">
                                    <SettingsText
                                        value={String(tooltipDurationMs)}
                                        onChange={(val) => handleChange('tooltip.durationMs', Number(val) || 1500)}
                                        maxLength={5}
                                    />
                                </SettingsRow>
                            ) : null}
                        </SettingsSection>
                    )}

                    {/* Regional */}
                    {anyMatch('Language', 'Time Zone', 'Date Format') && (
                        <SettingsSection title="Regional">
                            {matches('Language') || !search ? (
                                <SettingsRow label="Language">
                                    <SettingsDropdown value={language} onChange={(val) => handleChange('language', val)} options={LANGUAGE_OPTIONS} />
                                </SettingsRow>
                            ) : null}
                            {matches('Time Zone') || !search ? (
                                <SettingsRow label="Time Zone">
                                    <span className="settings-preview">{timeZone}</span>
                                </SettingsRow>
                            ) : null}
                            {matches('Date Format') || !search ? (
                                <SettingsRow label="Date Format">
                                    <SettingsDateFormat value={dateFormat} onEdit={() => setDateFormatDialogOpen(true)} />
                                </SettingsRow>
                            ) : null}
                        </SettingsSection>
                    )}

                    {/* Dashboard */}
                    {/* ADDED — start tab configuration */}
                    {anyMatch('Start Tab', 'Remember last tab') && (
                        <SettingsSection title="Dashboard">
                            {matches('Start Tab') || !search ? (
                                <SettingsRow label="Start Tab">
                                    <SettingsDropdown
                                        value={startTab}
                                        onChange={(val) => handleChange('dashboard.startTab', val)}
                                        options={START_TAB_OPTIONS}
                                    />
                                </SettingsRow>
                            ) : null}
                            {/* ADDED — toggle auto-remembering the last active tab vs a fixed pinned start tab */}
                            {matches('Remember last tab') || !search ? (
                                <SettingsRow label="Remember last tab">
                                    <SettingsToggle value={rememberLastTab} onChange={(val) => handleChange('dashboard.rememberLastTab', val)} />
                                </SettingsRow>
                            ) : null}
                        </SettingsSection>
                    )}

                    {/* ContentPanel */}
                    {anyMatch('Background', 'Auto-open JInspector', 'Close JInspector on leave', 'Chip Date Format', 'Dagdeel Nacht', 'Dagdeel Ochtend', 'Dagdeel Middag', 'Dagdeel Avond') && (
                        <SettingsSection title="Content Panel">
                            {matches('Background') || !search ? (
                                <SettingsRow label="Background">
                                    <SettingsDropdown value={bgMode} onChange={(val) => handleChange('contentPanel.bgMode', val)} options={BGMODE_OPTIONS} />
                                </SettingsRow>
                            ) : null}
                            {matches('Auto-open JInspector') || !search ? (
                                <SettingsRow label="Auto-open JInspector">
                                    <SettingsToggle value={autoOpenJInspector} onChange={(val) => handleChange('contentPanel.autoOpenJInspector', val)} />
                                </SettingsRow>
                            ) : null}
                            {/* ADDED — toggle to keep JInspector open on mouse leave */}
                            {matches('Close JInspector on leave') || !search ? (
                                <SettingsRow label="Close JInspector on leave">
                                    <SettingsToggle value={closeOnLeave} onChange={(val) => handleChange('contentPanel.closeJInspectorOnLeave', val)} />
                                </SettingsRow>
                            ) : null}
                            {matches('Chip Date Format') || !search ? (
                                <SettingsRow label="Chip Date Format">
                                    <SettingsDateFormat value={chipDateTimeFormat} onEdit={() => setChipDateTimeFormatDialogOpen(true)} />
                                </SettingsRow>
                            ) : null}
                            {matches('Dagdeel Nacht') || !search ? (
                                <SettingsRow label="Dagdeel Nacht">
                                    <SettingsText value={daypartNight} onChange={(val) => handleChange('contentPanel.daypart.night', val)} />
                                </SettingsRow>
                            ) : null}
                            {matches('Dagdeel Ochtend') || !search ? (
                                <SettingsRow label="Dagdeel Ochtend">
                                    <SettingsText value={daypartMorning} onChange={(val) => handleChange('contentPanel.daypart.morning', val)} />
                                </SettingsRow>
                            ) : null}
                            {matches('Dagdeel Middag') || !search ? (
                                <SettingsRow label="Dagdeel Middag">
                                    <SettingsText value={daypartAfternoon} onChange={(val) => handleChange('contentPanel.daypart.afternoon', val)} />
                                </SettingsRow>
                            ) : null}
                            {matches('Dagdeel Avond') || !search ? (
                                <SettingsRow label="Dagdeel Avond">
                                    <SettingsText value={daypartEvening} onChange={(val) => handleChange('contentPanel.daypart.evening', val)} />
                                </SettingsRow>
                            ) : null}
                        </SettingsSection>
                    )}

                    {/* Body Editor */}
                    {/* ADDED — style note selection for HTML note rendering */}
                    {anyMatch('Style Note') && (
                        <SettingsSection title="Body Editor">
                            {matches('Style Note') || !search ? (
                                <SettingsRow label="Style Note">
                                    <SettingsDropdown
                                        value={bodyEditorStyleNote}
                                        onChange={(val) => handleChange('bodyEditor.styleNote', val)}
                                        options={styleNoteOptions}
                                    />
                                </SettingsRow>
                            ) : null}
                        </SettingsSection>
                    )}

                    {/* Code Editor */}
                    {/* ADDED — JxCodeEditor theme preference, consumed by CsCodeTemplateEditorDlg etc. */}
                    {anyMatch('Theme') && (
                        <SettingsSection title="Code Editor">
                            {matches('Theme') || !search ? (
                                <SettingsRow label="Theme">
                                    <SettingsDropdown
                                        value={codeEditorTheme}
                                        onChange={(val) => handleChange('codeEditor.theme', val)}
                                        options={CODE_EDITOR_THEME_OPTIONS}
                                    />
                                </SettingsRow>
                            ) : null}
                        </SettingsSection>
                    )}

                    {/* Output Window */}
                    {/* ADDED — OutputMsgDlg date format + cache size */}
                    {anyMatch('Output Date Format', 'Output Cache Size') && (
                        <SettingsSection title="Output Window">
                            {matches('Output Date Format') || !search ? (
                                <SettingsRow label="Output Date Format">
                                    <SettingsDateFormat value={msgDateTimeFormat} onEdit={() => setMsgDateTimeFormatDialogOpen(true)} />
                                </SettingsRow>
                            ) : null}
                            {matches('Output Cache Size') || !search ? (
                                <SettingsRow label="Output Cache Size">
                                    <SettingsText
                                        value={String(msgCacheSize)}
                                        onChange={(val) => handleChange('msgWindow.cacheSize', Number(val) || 300)}
                                        maxLength={5}
                                    />
                                </SettingsRow>
                            ) : null}
                        </SettingsSection>
                    )}

                    {/* Uml Workspace */}
                    {/* ADDED — preview thumbnails toggle for the Load Uml Schema dialog + hover-debug-logging toggle */}
                    {anyMatch('Show Preview in Load Dialog', 'Log Hover Messages') && (
                        <SettingsSection title="Uml Workspace">
                            {matches('Show Preview in Load Dialog') || !search ? (
                                <SettingsRow label="Show Preview in Load Dialog">
                                    <SettingsToggle
                                        value={showPreviewInLoadDlg}
                                        onChange={(val) => handleChange('umlWorkspace.showPreviewInLoadDlg', val)}
                                    />
                                </SettingsRow>
                            ) : null}
                            {matches('Log Hover Messages') || !search ? (
                                <SettingsRow label="Log Hover Messages">
                                    <SettingsToggle
                                        value={logHoverMessages}
                                        onChange={(val) => handleChange('umlWorkspace.logHoverMessages', val)}
                                    />
                                </SettingsRow>
                            ) : null}
                        </SettingsSection>
                    )}

                </Box>
            </Box>

            <DateFormatDialog
                open={dateFormatDialogOpen}
                currentValue={dateFormat}
                onSave={handleDateFormatSave}
                onClose={() => setDateFormatDialogOpen(false)}
            />
            <DateFormatDialog
                open={chipDateTimeFormatDialogOpen}
                currentValue={chipDateTimeFormat}
                onSave={(val) => handleChange('contentPanel.chipDateTimeFormat', val)}
                onClose={() => setChipDateTimeFormatDialogOpen(false)}
            />
            <DateFormatDialog
                open={msgDateTimeFormatDialogOpen}
                currentValue={msgDateTimeFormat}
                onSave={(val) => handleChange('msgWindow.dateTimeFormat', val)}
                onClose={() => setMsgDateTimeFormatDialogOpen(false)}
            />
        </Box>
    );
}
